import { CSSResultGroup, html, LitElement, nothing } from 'lit'
import { customElement, property, state } from 'lit/decorators.js'
import { style } from './store-location.style.js'

import { t } from '@cloudsuite/mosaic-shared/translations/translator.js'
import { InternalStore, StoreModel } from 'components/discovery/store-locator/typing/store-locator.js'
import { isOpenNow } from 'components/discovery/store-locator/typing/business-hours.js'
import { initializeIconsByRole } from '@cloudsuite/mosaic-shared/icons/library.js'
import { hostStyle } from '@cloudsuite/mosaic-shared/styles/host.style.js'
import { HiddenFieldsMixin } from 'components/shared/hidden-fields/hidden-fields-mixin.js'


initializeIconsByRole({
  'csc-store-location.premium': { name: 'star', style: 'solid' },
  'csc-store-location.distance': { name: 'location-arrow', style: 'light' },
})

export const storeLocationOptionalFields = ['distance', 'status', 'premium', 'phone', 'opening-hours'] as const
export type StoreLocationOptionalField = typeof storeLocationOptionalFields[number]

export class StoreLocationSelectedEvent extends Event {
  static type = 'store-location-selected' as const

  store: InternalStore

  constructor(store: InternalStore) {
    super(StoreLocationSelectedEvent.type, { bubbles: true, composed: true })
    this.store = store
  }
}

@customElement('csc-store-location')
export class CSCStoreLocation extends HiddenFieldsMixin(LitElement, storeLocationOptionalFields) {
  static override styles: CSSResultGroup = [hostStyle, style]

  @property({ type: Object })
  store: InternalStore = new StoreModel().parseToInternal()

  @property({ type: Boolean, reflect: true })
  selected = false

  @state()
  private legacyHideDistance = false

  /** @deprecated Distance is shown by default when the store has one; use `hidden-fields="distance"` to hide it. */
  @property({ type: Boolean, attribute: 'show-distance' })
  set showDistance(value: boolean) {
    console.warn('<csc-store-location> "show-distance" is deprecated, distance is shown by default; use hidden-fields="distance" to hide it.')
    this.legacyHideDistance = !value
  }

  get showDistance() {
    return !this.legacyHideDistance && !this.isHidden('distance')
  }


  private handleSelect = () => {
    this.dispatchEvent(new StoreLocationSelectedEvent(this.store))
  }

  private get hasDistance(): boolean {
    return this.showDistance
    && (this.store.distanceInKm > 0
    || this.store.distanceInM > 0)
  }

  private get businessHours() {
    return this.store.businessHours ?? []
  }

  private formattedDistance = () => {
    return this.store.distanceInM >= 1000 ? `${this.store.distanceInKm} km` : `${this.store.distanceInM} m`
  }

  private renderImage = () => {
    if (!this.store.logo) return nothing

    return html`
      <cse-image source="${this.store.logo}" alt="${this.store.name}" width="64" height="64" fit="contain"></cse-image>
    `
  }

  private renderPremiumBadge = () => html`
    <cse-badge variant="premium" size="small">
      <cse-icon icon-role="csc-store-location.premium" slot="prefix"></cse-icon>
      ${t('store_location_premium_badge', { ns: 'csc-store-location', default: 'Premium' })}
    </cse-badge>
  `

  private renderOpenClosedBadge = (open: boolean) => html`
    <cse-badge variant="${open ? 'success' : 'error'}" composition="simple" size="small">
      <span id="divider" slot="prefix">•</span>
      ${open
        ? t('store_location_open_now', { ns: 'csc-store-location', default: 'Open' })
        : t('store_location_closed_now', { ns: 'csc-store-location', default: 'Closed' })}
    </cse-badge>
  `

  private renderNameRow = () => html`
    <cse-group justify="space-between" alignment="baseline">
      <cse-text id="name" variant="heading-6" as="h3">
        ${this.store.name}
      </cse-text>
      ${this.store.premium && !this.isHidden('premium') ? this.renderPremiumBadge() : nothing}
    </cse-group>
  `

  private renderStatusRow = () => {
    const hasStatus = this.businessHours.length > 0 && !this.isHidden('status')
    if (!this.hasDistance && !hasStatus) return nothing

    const open = hasStatus && isOpenNow(this.businessHours)

    return html`
      <cse-group alignment="baseline">
        ${this.hasDistance ? html`
          <cse-group id="distance" alignment="baseline" column-gap="05">
            <cse-icon icon-role="csc-store-location.distance" size="lg"></cse-icon>
            <cse-text variant="caption-l">${this.formattedDistance()}</cse-text>
          </cse-group>
        `: nothing}
        ${hasStatus ? this.renderOpenClosedBadge(open) : nothing}
      </cse-group>
    `
  }

  private renderAddress = () => {
    const address = this.store.address
    if (!address) return nothing

    return html`
      <cse-group id="address" direction="column" row-gap="00">
        <cse-text id="street" variant="caption-l">
          ${address.street} ${address.houseNumber}
        </cse-text>
        <cse-text id="zip" variant="caption-l">
          ${address.zipcode} ${address.city}
        </cse-text>
        <cse-text id="country" variant="caption-l">
          ${address.country}
        </cse-text>
      </cse-group>
    `
  }

  private renderPhone = () => {
    if (!this.store.phone || this.isHidden('phone')) return nothing

    return html`
      <cse-text variant="caption-l" id="phone">${this.store.phone}</cse-text>
    `
  }

  private handleAccordionClick = (event: Event) => {
    event.stopPropagation()
  }

  private renderOpeningHoursRows = () => this.businessHours.map((entry) => html`
    <cse-text variant="caption-l">${entry.name}</cse-text>
    <cse-text variant="caption-l">
      ${entry.timeFrom && entry.timeTo ? `${entry.timeFrom} - ${entry.timeTo}` : entry.text}
    </cse-text>
  `)

  private renderOpeningHours = () => {
    if (this.businessHours.length === 0 || this.isHidden('opening-hours')) return nothing

    return html`
    <div class="opening-hours-wrapper">
      <div class="opening-hours-grid opening-hours-sizer" aria-hidden="true">
        ${this.renderOpeningHoursRows()}
      </div>
      <cse-accordion
        id="opening-hours"
        variant="simple"
        @click="${this.handleAccordionClick}"
      >
        <div slot="title">
          <cse-text class="opening-hours-title" as="span">
            ${t('store_location_opening_hours', { ns: 'csc-store-location', default: 'Opening hours' })}
          </cse-text>
        </div>
        <div class="opening-hours-grid" slot="content">
          ${this.renderOpeningHoursRows()}
        </div>
      </cse-accordion>
  </div>
    `
  }

  override render() {
    if (!this.store) return nothing

    return html`
      <cse-selectable
        class="csc-store-location"
        @click="${this.handleSelect}"
        state="${this.selected ? 'selected' : 'selectable'}"
      >
        <cse-group justify="space-between">
          <cse-group id="details" direction="column">
            ${this.renderNameRow()}
            ${this.renderStatusRow()}
            ${this.renderAddress()}
            ${this.renderPhone()}
            ${this.renderOpeningHours()}
            <slot name="stock"></slot>
          </cse-group>
          ${this.renderImage()}
        </cse-group>
      </cse-selectable>
    `
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'csc-store-location': CSCStoreLocation
  }

  interface GlobalEventHandlersEventMap {
    [StoreLocationSelectedEvent.type]: StoreLocationSelectedEvent
  }
}
